import React, { useEffect, useRef } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, {
    Easing,
    Extrapolation,
    SharedValue,
    interpolate,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withTiming,
} from 'react-native-reanimated';
import Svg, { Line } from 'react-native-svg';

import { CloseIconPlayerStats } from '@/assets/close-icon-player-stats';
import { CheckmarkPlayerStats } from '@/assets/icons/checkmark-player-stats';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { withOpacity } from '@/styles/styles';
import { PlayerRecentStat } from '@/types/api.generated';
import { format } from 'date-fns';

// ReferenceProjectionLine constants
const OUTER_PADDING = 16;
const INNER_PADDING = 16;
const DESIGN_DASH_WIDTH = 2.4;
const GAP = 4;
const HEIGHT = 1;

// Chart constants
const PADDING_BOTTOM_CHART = 16;
const PADDING_TOP_CHART = 6;
const CHART_HEIGHT = 144;
const MAX_BAR_HEIGHT = 80;
export const MIN_TOTAL_HEIGHT_CHART = CHART_HEIGHT + PADDING_BOTTOM_CHART + PADDING_TOP_CHART;

const REFERENCE_LINE_ANIMATION_CONFIG = {
    duration: 1300,
    easing: Easing.out(Easing.exp),
};

const REFERENCE_LINE_INITIAL_ANIMATION_CONFIG = {
    duration: 1000,
    easing: Easing.out(Easing.exp),
};

const CHART_ANIMATION_CONFIG = {
    duration: 600,
    easing: Easing.out(Easing.exp),
};

export const PlayerRecentStats = ({
    playerStats,
    selectedProjectionValue,
    maxValue,
    activeAlternateIndex,
}: {
    playerStats: PlayerRecentStat[];
    selectedProjectionValue: number;
    maxValue: number;
    activeAlternateIndex: number;
}) => {
    const chartProgress = useSharedValue(0);
    const referenceLineProgress = useSharedValue(0);
    const previousActiveAlternateIndex = useRef(activeAlternateIndex);

    useEffect(() => {
        if (selectedProjectionValue == null) return;
        const indexChanged = previousActiveAlternateIndex.current !== activeAlternateIndex;

        if (chartProgress.value === 0) {
            // First render => animate both chart and reference line
            chartProgress.value = withDelay(150, withTiming(1, CHART_ANIMATION_CONFIG));

            referenceLineProgress.value = withDelay(300, withTiming(1, REFERENCE_LINE_INITIAL_ANIMATION_CONFIG));
        } else if (indexChanged) {
            // Projection index changed => animate reference line bottom to top
            referenceLineProgress.value = 0;
            referenceLineProgress.value = withTiming(1, REFERENCE_LINE_ANIMATION_CONFIG);
        } else {
            // Same projection selected => update instantly
            referenceLineProgress.value = withTiming(1, REFERENCE_LINE_ANIMATION_CONFIG);
        }

        previousActiveAlternateIndex.current = activeAlternateIndex;
    }, [
        selectedProjectionValue,
        chartProgress,
        referenceLineProgress,
        previousActiveAlternateIndex,
        activeAlternateIndex,
    ]);

    const chartMaxValue = Math.max(maxValue, selectedProjectionValue);
    const line = (selectedProjectionValue / chartMaxValue) * MAX_BAR_HEIGHT;
    const currentProjValueLineFromBottom = Math.min(line, MAX_BAR_HEIGHT);
    return (
        <Row
            paddingHorizontal={'s16'}
            style={styles.chartContainer}
            gap={'s8'}
            alignItems={'flex-end'}
            justifyContent={'space-around'}
            minHeight={MIN_TOTAL_HEIGHT_CHART}
        >
            <ReferenceProjectionLine
                lineValue={currentProjValueLineFromBottom}
                referenceLineProgress={referenceLineProgress}
            />
            {playerStats.map((stat, index) => (
                <PlayerRecentStatContent
                    key={`${stat.date}-${stat.value}-${index}`}
                    stat={stat}
                    chartMaxValue={chartMaxValue}
                    selectedProjectionValue={selectedProjectionValue}
                    chartProgress={chartProgress}
                />
            ))}
        </Row>
    );
};

const PlayerRecentStatContent = ({
    stat,
    chartMaxValue,
    selectedProjectionValue,
    chartProgress,
}: {
    stat: PlayerRecentStat;
    chartMaxValue: number;
    selectedProjectionValue: number;
    chartProgress: SharedValue<number>;
}) => {
    const statValue = typeof stat.value === 'undefined' ? 0 : stat.value;
    const barHeight = (statValue / chartMaxValue) * MAX_BAR_HEIGHT;
    const animatedStyle = useAnimatedStyle(() => {
        const delayedProgress = interpolate(chartProgress.value, [0.7, 1], [0, 1], Extrapolation.CLAMP);

        return {
            height: barHeight * delayedProgress,
        };
    });
    const noValueForStat = stat.value <= 0 || stat.value === undefined;
    const backgroundColor =
        stat.value > selectedProjectionValue
            ? withOpacity(ds.colorValue.background.success.strong, 0.15)
            : withOpacity(ds.colorValue.background.danger.strong, 0.15);
    const borderColor =
        stat.value > selectedProjectionValue ? ds.colorValue.border.strong : ds.colorValue.border.subtle;
    const borderWidth = noValueForStat ? 0 : 1;
    const date = stat.date === null ? '--' : format(new Date(stat.date), 'MM/dd');

    return (
        <Box alignItems={'center'} justifyContent={'flex-end'} flex={1}>
            <Animated.View style={[styles.barChart, { backgroundColor, borderColor, borderWidth }, animatedStyle]}>
                {stat.value === undefined ? null : stat.value > selectedProjectionValue ? (
                    <CheckmarkPlayerStats />
                ) : (
                    <CloseIconPlayerStats />
                )}
            </Animated.View>
            <Text variant={'titleSmall'} letterSpacing={-0.08} color={ds.colorToken.text.primary} marginBottom={'s2'}>
                {stat.value ?? '--'}
            </Text>
            <Text
                variant={'bodySmaller'}
                lineHeight={16}
                color={'neutral300'}
                marginBottom={'s2'}
                numberOfLines={1}
                ellipsizeMode={'tail'}
            >
                {stat.matchupDescription}
            </Text>
            <Text variant={'bodySmaller'} lineHeight={16} color={'neutral300'}>
                {date}
            </Text>
        </Box>
    );
};

const ReferenceProjectionLine = ({
    lineValue,
    referenceLineProgress,
}: {
    lineValue: number;
    referenceLineProgress: SharedValue<number>;
}) => {
    const { width: screenWidth } = useWindowDimensions();
    const dividerWidth = screenWidth - 2 * OUTER_PADDING - 2 * INNER_PADDING;
    const dashCount = Math.floor((dividerWidth + GAP) / (DESIGN_DASH_WIDTH + GAP));
    const dashLength = (dividerWidth - GAP * (dashCount - 1)) / dashCount;
    const baseline = CHART_HEIGHT - MAX_BAR_HEIGHT;
    const target = PADDING_BOTTOM_CHART + CHART_HEIGHT + lineValue - MAX_BAR_HEIGHT;

    const animatedStyle = useAnimatedStyle(() => {
        return {
            bottom: interpolate(referenceLineProgress.value, [0, 1], [baseline, target], Extrapolation.CLAMP),
        };
    });

    return (
        <Animated.View style={[styles.dividerContainer, { width: dividerWidth, height: HEIGHT }, animatedStyle]}>
            <Svg style={StyleSheet.absoluteFill} height={HEIGHT} width={dividerWidth}>
                {Array.from({ length: dashCount }).map((_, i) => {
                    const x = i * (dashLength + GAP);
                    return (
                        <Line
                            key={i}
                            x1={x}
                            y1={HEIGHT / 2}
                            x2={x + dashLength}
                            y2={HEIGHT / 2}
                            stroke={withOpacity(ds.colorValue.icon.primary, 0.25)}
                            strokeWidth={HEIGHT}
                        />
                    );
                })}
            </Svg>
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    chartContainer: {
        position: 'relative',
        paddingBottom: PADDING_BOTTOM_CHART,
        paddingTop: PADDING_TOP_CHART,
    },
    dividerContainer: {
        position: 'absolute',
        left: OUTER_PADDING,
        borderRadius: 0.5,
        zIndex: 1,
    },
    barChart: {
        width: 36,
        marginBottom: 8,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
